import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppLayoutContextConsumer } from '@occc-app/design-system';
import {
  useGetCampaignName,
  getCreatePageLinks as getCreateCampaignManagementPageLinks,
} from '@occc-app/routes/CampaignComponents/utils';
import { getCreatePageLinks as getCreateProgramManagementPageLinks } from '@occc-app/routes/ProgramManagement/utils';
import { Offer_Engagement_Type } from '@occc/graphql';
import { OfferType } from '@occc-app/offerValidationService/types';
import { CampaignComponentTabs } from '@occc-app/routes/CampaignComponents/components/ConfigurationLayout';

import { useChainId } from '@occc-app/Chain/hooks';
import { Flow_Types } from '@occc-app/offerHelpers/types';
import { OfferCreateConfigurePage } from '@occc-app/routes/Offer/OfferCreatePage/OfferCreateConfigurePage';
import { useNavigateToCampaign } from '@occc-app/offerHelpers/hooks/useNavigateToCampaign';
import { getConfigureFormInitialValues } from '../GameConfigureForm/utils/configureFormInitialValues';
import { GameConfigureFormValues } from '../types';
import { createOffersFilterMaker } from '../GameConfigureForm/GameConfigureFormContainer';
import { campaignManagementGameRouteCreator, programManagementGameRouteCreator } from '../utils';
import { messages } from '../messages';
import { ConfigureForm } from '../GameConfigureForm/ConfigureForm';
import { getConfigureFormOperations } from '../GameConfigureForm/utils/configureFormOperations';

type CampaignManagementGameCreateParams = {
  id: string;
};

export const CampaignManagementGameCreatePageContainer = () => {
  const navigate = useNavigate();
  const { id: campaignId = '' } = useParams<CampaignManagementGameCreateParams>();

  const chainId = useChainId();
  const campaignName = useGetCampaignName(chainId, campaignId);
  const navigateToCampaign = useNavigateToCampaign(campaignId);

  const links = useMemo(
    () => getCreateCampaignManagementPageLinks(campaignId, campaignName, 'New Game'),
    [campaignId, campaignName],
  );
  useAppLayoutContextConsumer(links);

  const makeOffersFilter = useMemo(() => createOffersFilterMaker(chainId), [chainId]);

  return (
    <OfferCreateConfigurePage<GameConfigureFormValues, GameConfigureFormValues>
      pageTitle='Campaign > Create'
      subtitle={messages.subtitle}
      offerType={OfferType.FreeGame}
      engagementType={Offer_Engagement_Type.Game}
      campaignId={campaignId}
      getConfigureOperations={getConfigureFormOperations}
      getInitialValues={getConfigureFormInitialValues}
      onOfferCreated={(offerId) =>
        navigate(
          campaignManagementGameRouteCreator.configureWithTab(
            campaignId,
            offerId,
            CampaignComponentTabs.Target,
          ),
        )
      }
      renderConfigureForm={({ onSubmit, initialValues, loading }) => (
        <ConfigureForm
          offerType={OfferType.FreeGame}
          onSubmit={onSubmit}
          initialValues={initialValues}
          loading={loading}
          onCancelClick={navigateToCampaign}
          getOffersFilterMaker={makeOffersFilter}
          currentFlowType={Flow_Types.Configure}
          isFormEditable
        />
      )}
    />
  );
};

export const ProgramManagementGameCreatePageContainer = () => {
  const navigate = useNavigate();

  const chainId = useChainId();

  const links = useMemo(
    () => getCreateProgramManagementPageLinks('New Game', OfferType.VoucheredGame),
    [],
  );
  useAppLayoutContextConsumer(links);

  const makeOffersFilter = useMemo(() => createOffersFilterMaker(chainId), [chainId]);

  return (
    <OfferCreateConfigurePage<GameConfigureFormValues, GameConfigureFormValues>
      pageTitle='Program Management > Create'
      subtitle={messages.subtitle}
      offerType={OfferType.VoucheredGame}
      engagementType={Offer_Engagement_Type.GameVoucher}
      getConfigureOperations={getConfigureFormOperations}
      getInitialValues={getConfigureFormInitialValues}
      onOfferCreated={(offerId) =>
        navigate(
          programManagementGameRouteCreator.configureWithTab(offerId, CampaignComponentTabs.Target),
        )
      }
      renderConfigureForm={({ onSubmit, initialValues, loading }) => (
        <ConfigureForm
          offerType={OfferType.VoucheredGame}
          onSubmit={onSubmit}
          initialValues={initialValues}
          loading={loading}
          onCancelClick={() => navigate(programManagementGameRouteCreator.base())}
          getOffersFilterMaker={makeOffersFilter}
          currentFlowType={Flow_Types.Configure}
          isFormEditable
        />
      )}
    />
  );
};
